import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { Link, useNavigate, useParams } from 'react-router'
import {
  Activity,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  Archive,
  BookOpenText,
  Clock3,
  Copy,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  NotebookText,
  Paperclip,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  ShieldAlert,
  Trash2,
  UserRound,
  Users,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../../../../contexts/AuthContext'
import { Alert, AlertDescription } from '../../../../../components/ui/alert'
import { Badge } from '../../../../../components/ui/badge'
import { Button } from '../../../../../components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog'
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '../../../../../components/ui/drawer'
import { Input } from '../../../../../components/ui/input'
import { NavigationMenu, NavigationMenuItem, NavigationMenuList } from '../../../../../components/ui/navigation-menu.component'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select'
import { Skeleton } from '../../../../../components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../../../components/ui/pagination'
import { FilePreview, isPreviewableFile } from '../../../../../components/FilePreview'
import { ImagePreview } from '../../../../../components/ImagePreview'
import { isDarkAtom } from '../../../../../state/store'
import { buildStorageFileUrl } from '../../../../../state/types'
import { cn } from '../../../../../lib/utils'
import { getClassroomPalette } from '../../../../../lib/classroom-theme'
import { resolveStudentMascotDisplay, resolveStudentNameDisplay, resolveStudentUsernameDisplay } from '../../../../../lib/student-display'
import { formatClassroomStatusLabel } from '../../components'
import { useWorkspace } from '../../../../../contexts/WorkspaceContext'
import {
  CLASS_DETAIL_STUDENT_PAGE_SIZE,
  buildClassStudentPageNumbers,
  clampClassStudentPage,
  getClassStudentPageCount,
  getPaginatedClassStudents,
  shouldShowClassStudentPagination,
} from './studentPagination'
import {
  createTeacher,
  fetchClassChats,
  fetchClassStudents,
  fetchClassroom,
  fetchCreditUsage,
  fetchMembers,
  fetchFlaggedList,
  uploadFile,
  updateClassroom,
} from '../../../../../api/backend'
import type {
  Classroom,
  ClassroomMaterial,
  ClassroomMaterialAttachment,
  CreditUsageResponse,
  FlaggedItem,
  StarterPrompt,
  StudentItem,
  AgeTier,
  CreateMemberPayload,
} from '../../../../../api/backend'

type DetailTab = 'stream' | 'classwork' | 'people'
type MaterialKind = 'NOTE' | 'REFERENCE' | 'WORKSHEET'
type PromptDraft = StarterPrompt & { id: string }
const TOPIC_LIMIT = 5
const TOPIC_CHAR_LIMIT = 20
const CREATE_TEACHER_VALUE = '__create_new_teacher__'

const AGE_TIER_LABELS: Record<AgeTier, string> = {
  UNDER_13: 'Under 13',
  TEEN: 'Teen',
}

function getHeroForegroundMode(backgroundColor: string) {
  const match = backgroundColor.match(/hsl\(\s*\d+\s+\d+%\s+(\d+)%\s*\)/i)
  const lightness = match ? Number(match[1]) : 50
  return lightness >= 68 ? 'dark' : 'light'
}

function genTempPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
}

function statusTone(status: string) {
  const normalized = status.toUpperCase()
  if (normalized === 'RESTRICTED') return 'danger'
  if (normalized === 'WARNING') return 'warning'
  if (normalized === 'ATTENTION') return 'attention'
  return 'success'
}

function formatShortDate(value?: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function MetricCard({
  label,
  value,
  className,
}: {
  label: string
  value: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('rounded-[1.1rem] border-2 border-nb-border bg-bg px-4 py-3', className)}>
      <div className="text-[11px] font-heading uppercase tracking-[0.14em] text-foreground/45">{label}</div>
      <div className="mt-2 font-heading text-[2rem] leading-none text-foreground">{value}</div>
    </div>
  )
}

function ContentCard({
  title,
  children,
  className,
}: {
  title: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('rounded-[1.4rem] border-2 border-nb-border bg-bg p-4 nb-shadow', className)}>
      <div className="mb-4 text-sm font-heading text-foreground/65">{title}</div>
      {children}
    </div>
  )
}

function ClassDetailSkeleton() {
  return (
    <div className="p-6">
      <div className="mb-6 flex items-center gap-5">
        <Skeleton className="h-10 w-12 rounded-[0.9rem]" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-24 rounded-md" />
          <Skeleton className="h-5 w-72 rounded-md" />
        </div>
      </div>

      <div className="mb-6 rounded-[1.4rem] border-[3px] border-nb-border p-5 nb-shadow">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1 space-y-3">
            <Skeleton className="h-3 w-24 rounded-md" />
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-56 rounded-md" />
              <Skeleton className="h-9 w-9 rounded-[0.9rem]" />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Skeleton className="h-6 w-28 rounded-full" />
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-32 rounded-full" />
            </div>
          </div>
          <div className="min-w-[220px] rounded-[1.1rem] border-2 border-nb-border bg-background p-4">
            <Skeleton className="h-3 w-20 rounded-md" />
            <Skeleton className="mt-3 h-10 w-36 rounded-md" />
            <Skeleton className="mt-3 h-4 w-40 rounded-md" />
            <Skeleton className="mt-4 h-9 w-28 rounded-[0.9rem]" />
          </div>
        </div>
      </div>

      <div className="mb-6 flex gap-2">
        <Skeleton className="h-10 w-24 rounded-base" />
        <Skeleton className="h-10 w-28 rounded-base" />
        <Skeleton className="h-10 w-20 rounded-base" />
      </div>

      <div className="space-y-4">
        <div className="rounded-[1.4rem] border-2 border-nb-border bg-bg p-4 nb-shadow">
          <Skeleton className="mb-2 h-5 w-40 rounded-md" />
          <Skeleton className="mb-4 h-4 w-72 rounded-md" />
          <div className="grid gap-3 lg:grid-cols-3">
            <Skeleton className="h-20 rounded-[1.1rem]" />
            <Skeleton className="h-20 rounded-[1.1rem]" />
            <Skeleton className="h-20 rounded-[1.1rem]" />
          </div>
        </div>

        <div className="rounded-[1.4rem] border-2 border-nb-border bg-bg p-4 nb-shadow">
          <Skeleton className="mb-2 h-5 w-32 rounded-md" />
          <Skeleton className="mb-4 h-4 w-64 rounded-md" />
          <div className="grid gap-3 md:grid-cols-3">
            <Skeleton className="h-20 rounded-[1.1rem]" />
            <Skeleton className="h-20 rounded-[1.1rem]" />
            <div />
          </div>
        </div>
      </div>
    </div>
  )
}

function createPromptDraft(prompt?: StarterPrompt): PromptDraft {
  return {
    id: crypto.randomUUID(),
    label: prompt?.label ?? '',
    prompt: prompt?.prompt ?? '',
  }
}

function isPromptComplete(prompt?: PromptDraft | null) {
  if (!prompt) return false
  return Boolean(prompt.label.trim() && prompt.prompt.trim())
}

function MaterialTypeIcon({ type }: { type?: string | null }) {
  if (type?.toUpperCase() === 'WORKSHEET') return <NotebookText className="size-4" />
  if (type?.toUpperCase() === 'REFERENCE') return <BookOpenText className="size-4" />
  return <FileText className="size-4" />
}

function materialKindLabel(type?: string | null) {
  if (!type) return 'Note'
  const normalized = type.toUpperCase()
  if (normalized === 'REFERENCE') return 'Reference'
  if (normalized === 'WORKSHEET') return 'Worksheet'
  return 'Note'
}

function materialKindBadgeClass(type?: string | null) {
  const normalized = type?.toUpperCase() ?? 'NOTE'
  if (normalized === 'REFERENCE') {
    return 'border-emerald-400 bg-emerald-100/80 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
  }
  if (normalized === 'WORKSHEET') {
    return 'border-amber-400 bg-amber-100/80 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
  }
  return 'border-sky-400 bg-sky-100/80 text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300'
}

function materialKindIconClass(type?: string | null) {
  const normalized = type?.toUpperCase() ?? 'NOTE'
  if (normalized === 'REFERENCE') {
    return 'text-emerald-700 dark:text-emerald-300'
  }
  if (normalized === 'WORKSHEET') {
    return 'text-amber-700 dark:text-amber-300'
  }
  return 'text-sky-700 dark:text-sky-300'
}

function materialKindIconContainerClass(type?: string | null) {
  const normalized = type?.toUpperCase() ?? 'NOTE'
  if (normalized === 'REFERENCE') {
    return 'bg-emerald-50 dark:bg-emerald-950/30'
  }
  if (normalized === 'WORKSHEET') {
    return 'bg-amber-50 dark:bg-amber-950/30'
  }
  return 'bg-sky-50 dark:bg-sky-950/30'
}

export default function ClassSettingsDetailPage() {
  const { classId } = useParams<{ classId: string }>()
  const navigate = useNavigate()
  const isDark = useAtomValue(isDarkAtom)
  const { getAccessToken } = useAuth()
  const { memberRole } = useWorkspace()
  const [activeTab, setActiveTab] = useState<DetailTab>('stream')
  const [classroom, setClassroom] = useState<Classroom | null>(null)
  const [students, setStudents] = useState<StudentItem[]>([])
  const [teachers, setTeachers] = useState<StudentItem[]>([])
  const [usage, setUsage] = useState<CreditUsageResponse | null>(null)
  const [flaggedItems, setFlaggedItems] = useState<FlaggedItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [studentQuery, setStudentQuery] = useState('')
  const [studentPage, setStudentPage] = useState(0)
  const [topicInput, setTopicInput] = useState('')
  const [topicsDraft, setTopicsDraft] = useState<string[]>([])
  const [starterPromptsDraft, setStarterPromptsDraft] = useState<PromptDraft[]>([])
  const [materialsDraft, setMaterialsDraft] = useState<ClassroomMaterial[]>([])
  const [showMaterialForm, setShowMaterialForm] = useState(false)
  const [materialTitle, setMaterialTitle] = useState('')
  const [materialContent, setMaterialContent] = useState('')
  const [materialKind, setMaterialKind] = useState<MaterialKind>('NOTE')
  const [materialAttachment, setMaterialAttachment] = useState<ClassroomMaterialAttachment | null>(null)
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null)
  const [filePreview, setFilePreview] = useState<{ src: string; name: string; mimeType: string } | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isUploadingMaterialAttachment, setIsUploadingMaterialAttachment] = useState(false)
  const [isSavingTopics, setIsSavingTopics] = useState(false)
  const [isSavingPrompts, setIsSavingPrompts] = useState(false)
  const [isSavingMaterials, setIsSavingMaterials] = useState(false)
  const [renameDraft, setRenameDraft] = useState('')
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [isSavingRename, setIsSavingRename] = useState(false)
  const [teacherDraftId, setTeacherDraftId] = useState('unassigned')
  const [isSavingTeacher, setIsSavingTeacher] = useState(false)
  const [createTeacherDrawerOpen, setCreateTeacherDrawerOpen] = useState(false)
  const [teacherFirstName, setTeacherFirstName] = useState('')
  const [teacherLastName, setTeacherLastName] = useState('')
  const [teacherUsername, setTeacherUsername] = useState('')
  const [teacherPassword, setTeacherPassword] = useState('')
  const [isCreatingTeacher, setIsCreatingTeacher] = useState(false)

  const renderSavingTitle = useCallback((label: string, isSaving: boolean) => (
    <span className="inline-flex items-center gap-2">
      <span>{label}</span>
      {isSaving ? <LoaderCircle className="size-4 animate-spin text-foreground/45" /> : null}
    </span>
  ), [])

  const load = useCallback(() => {
    const token = getAccessToken()
    if (!token || !classId) return
    setIsLoading(true)
    setError(null)

    Promise.all([
      fetchClassroom(token, classId),
      fetchClassStudents(token, classId),
      fetchMembers(token, 'TEACHER', 0, 100),
      fetchClassChats(token, classId),
      fetchCreditUsage(token, '30d'),
      fetchFlaggedList(token),
    ])
      .then(([classroomResponse, studentsResponse, teacherResponse, chatsResponse, usageResponse, flaggedResponse]) => {
        const promptDrafts = (classroomResponse.starterPrompts ?? []).map(createPromptDraft)
        setClassroom(classroomResponse)
        setStudents(studentsResponse)
        setTeachers(teacherResponse.content)
        setUsage(usageResponse)
        setFlaggedItems(
          flaggedResponse.filter(
            (item) => item.classId === classId || chatsResponse.some((chat) => chat.id === item.chatId),
          ),
        )
        setTopicsDraft(classroomResponse.topics)
        setStarterPromptsDraft(promptDrafts)
        setMaterialsDraft(classroomResponse.materials ?? [])
        setRenameDraft(classroomResponse.title)
        setTeacherDraftId(classroomResponse.primaryTeacherId ?? 'unassigned')
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load class settings')
      })
      .finally(() => setIsLoading(false))
  }, [classId, getAccessToken])

  useEffect(() => {
    load()
  }, [load])

  const visibleStudents = useMemo(() => {
    const query = studentQuery.trim().toLowerCase()
    if (!query) return students
    return students.filter((student) => `${student.name} ${student.username}`.toLowerCase().includes(query))
  }, [studentQuery, students])
  const studentPageCount = getClassStudentPageCount(visibleStudents.length)
  const safeStudentPage = clampClassStudentPage(studentPage, visibleStudents.length)
  const studentPageNumbers = useMemo(
    () => buildClassStudentPageNumbers(studentPageCount, safeStudentPage),
    [safeStudentPage, studentPageCount],
  )
  const paginatedStudents = useMemo(
    () => getPaginatedClassStudents(visibleStudents, safeStudentPage),
    [safeStudentPage, visibleStudents],
  )
  const studentPageStart = visibleStudents.length === 0
    ? 0
    : safeStudentPage * CLASS_DETAIL_STUDENT_PAGE_SIZE + 1
  const studentPageEnd = Math.min(visibleStudents.length, studentPageStart + CLASS_DETAIL_STUDENT_PAGE_SIZE - 1)

  useEffect(() => {
    if (studentPage !== safeStudentPage) {
      setStudentPage(safeStudentPage)
    }
  }, [safeStudentPage, studentPage])

  const visibleRatio = `${students.filter((student) => student.isActive).length} / ${students.length || 0}`
  const classUsageCredits = useMemo(() => {
    const byClassCredits = usage?.byClass.find((item) => item.classId === classId)?.creditsUsed
    if (typeof byClassCredits === 'number') return byClassCredits
    return students.reduce((sum, student) => sum + (student.credits ?? 0), 0)
  }, [classId, students, usage?.byClass])

  const teacherLabel = useMemo(() => {
    if (classroom?.teacherName) return `Teacher: ${classroom.teacherName}`
    if (classroom?.primaryTeacherId) return `Teacher: ${classroom.primaryTeacherId}`
    return 'Teacher: Unassigned'
  }, [classroom?.primaryTeacherId, classroom?.teacherName])
  const classroomPalette = useMemo(
    () => (classroom ? getClassroomPalette(classroom) : null),
    [classroom],
  )
  const heroBackground = classroomPalette ? (isDark ? classroomPalette.dark : classroomPalette.light) : null
  const heroForegroundMode = useMemo(
    () => (heroBackground ? getHeroForegroundMode(heroBackground) : 'light'),
    [heroBackground],
  )
  const heroTitleClass = heroForegroundMode === 'dark' ? 'text-[#202114]' : 'text-white'
  const heroSubtitleClass = heroForegroundMode === 'dark' ? 'text-[#202114]/68' : 'text-white/72'
  const heroEyebrowClass = heroForegroundMode === 'dark' ? 'text-[#202114]/52' : 'text-white/56'
  const heroBadgeClass = heroForegroundMode === 'dark'
    ? 'border-black/14 bg-white/38 text-[#202114]'
    : 'border-black/25 bg-black/12 text-white'
  const isAdmin = memberRole === 'ADMIN'
  const statusMeta = classroom?.status === 'IN_PROGRESS'
    ? { Icon: Activity, label: formatClassroomStatusLabel(classroom.status) }
    : classroom?.status === 'ENDED'
      ? { Icon: Archive, label: formatClassroomStatusLabel(classroom.status) }
      : { Icon: Clock3, label: classroom ? formatClassroomStatusLabel(classroom.status) : 'Preparing' }

  const persistContentDraft = useCallback(async (
    next: {
      topics?: string[]
      starterPrompts?: PromptDraft[]
      materials?: ClassroomMaterial[]
    },
    section: 'topics' | 'prompts' | 'materials',
  ) => {
    const token = getAccessToken()
    if (!token || !classroom) return null

    const nextTopics = next.topics ?? topicsDraft
    const nextPromptDrafts = next.starterPrompts ?? starterPromptsDraft
    const nextMaterials = next.materials ?? materialsDraft
    const nextStarterPrompts = nextPromptDrafts.map(({ id, ...prompt }) => prompt)

    const topicsUnchanged = JSON.stringify(classroom.topics ?? []) === JSON.stringify(nextTopics)
    const promptsUnchanged = JSON.stringify(classroom.starterPrompts ?? []) === JSON.stringify(nextStarterPrompts)
    const materialsUnchanged = JSON.stringify(classroom.materials ?? []) === JSON.stringify(nextMaterials)

    if (topicsUnchanged && promptsUnchanged && materialsUnchanged) {
      return classroom
    }

    const setSavingState = section === 'topics'
      ? setIsSavingTopics
      : section === 'prompts'
        ? setIsSavingPrompts
        : setIsSavingMaterials

    setSavingState(true)
    try {
      const updated = await updateClassroom(token, classroom.id, {
        topics: nextTopics,
        starterPrompts: nextStarterPrompts,
        materials: nextMaterials,
      })
      setClassroom(updated)
      setTopicsDraft(updated.topics)
      setStarterPromptsDraft((updated.starterPrompts ?? []).map(createPromptDraft))
      setMaterialsDraft(updated.materials ?? [])
      return updated
    } finally {
      setSavingState(false)
    }
  }, [classroom, getAccessToken, materialsDraft, starterPromptsDraft, topicsDraft])

  const addTopic = async () => {
    if (topicsDraft.length >= TOPIC_LIMIT) {
      toast.error(`You can add up to ${TOPIC_LIMIT} topics.`)
      return
    }
    const next = topicInput.trim().slice(0, TOPIC_CHAR_LIMIT)
    if (!next || topicsDraft.includes(next)) return
    const nextTopics = [...topicsDraft, next]
    setTopicsDraft(nextTopics)
    setTopicInput('')
    try {
      await persistContentDraft({ topics: nextTopics }, 'topics')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update topics')
    }
  }

  const resetMaterialForm = () => {
    setMaterialTitle('')
    setMaterialContent('')
    setMaterialKind('NOTE')
    setMaterialAttachment(null)
    setEditingMaterialIndex(null)
    setShowMaterialForm(false)
  }

  const handleEditMaterial = (material: ClassroomMaterial, index: number) => {
    setEditingMaterialIndex(index)
    setMaterialTitle(material.title)
    setMaterialContent(material.content ?? '')
    setMaterialKind((material.kind?.toUpperCase() as MaterialKind) || 'NOTE')
    setMaterialAttachment(material.attachment ?? null)
    setShowMaterialForm(true)
  }

  const handleSaveMaterial = async () => {
    if (!materialTitle.trim()) return
    if (!materialAttachment) {
      toast.error('Attachment is required for materials.')
      return
    }
    const nextMaterial: ClassroomMaterial = {
      id: editingMaterialIndex != null ? materialsDraft[editingMaterialIndex]?.id ?? crypto.randomUUID() : crypto.randomUUID(),
      title: materialTitle.trim(),
      kind: materialKind,
      content: materialContent.trim() || undefined,
      attachment: materialAttachment,
      createdAt: editingMaterialIndex != null
        ? materialsDraft[editingMaterialIndex]?.createdAt ?? new Date().toISOString()
        : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    const nextMaterials = (() => {
      if (editingMaterialIndex != null) {
        return materialsDraft.map((item, itemIndex) => (itemIndex === editingMaterialIndex ? nextMaterial : item))
      }
      return [...materialsDraft, nextMaterial]
    })()

    try {
      await persistContentDraft({ materials: nextMaterials }, 'materials')
      resetMaterialForm()
      toast.success(editingMaterialIndex != null ? 'Material updated' : 'Material added')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save material')
    }
  }

  const handleDeleteMaterial = async (index: number) => {
    const nextMaterials = materialsDraft.filter((_, itemIndex) => itemIndex !== index)

    try {
      await persistContentDraft({ materials: nextMaterials }, 'materials')
      if (editingMaterialIndex === index) {
        resetMaterialForm()
      } else if (editingMaterialIndex != null && editingMaterialIndex > index) {
        setEditingMaterialIndex(editingMaterialIndex - 1)
      }
      toast.success('Material deleted')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete material')
    }
  }

  const openMaterialPreview = useCallback((attachment: ClassroomMaterialAttachment) => {
    const src = buildStorageFileUrl(attachment.storageName || attachment.storageId)
    if (attachment.mimeType.startsWith('image/')) {
      setImagePreview(src)
      return
    }
    if (!isPreviewableFile(attachment.mimeType, attachment.filename)) return
    setFilePreview({
      src,
      name: attachment.filename,
      mimeType: attachment.mimeType,
    })
  }, [])

  const handleMaterialAttachmentUpload = async () => {
    const token = getAccessToken()
    if (!token) return

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*,application/pdf,.pdf,.txt,.md,.json,.csv,.xml,.html,.js,.ts,.tsx,.jsx,.py,.java,.c,.cpp,.go,.rs,.sh,.yaml,.yml,.toml,.ini,.cfg,.log,.sql'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      if (file.size > 1024 * 1024) {
        toast.error('Material attachments must be 1 MB or smaller.')
        return
      }

      setIsUploadingMaterialAttachment(true)
      try {
        const uploaded = await uploadFile(token, file)
        setMaterialAttachment({
          storageId: uploaded.id,
          filename: uploaded.originalName,
          mimeType: uploaded.fileType,
          storageName: uploaded.storageName,
          sizeBytes: uploaded.fileSize,
        })
        toast.success('Attachment uploaded')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to upload material attachment')
      } finally {
        setIsUploadingMaterialAttachment(false)
      }
    }
    input.click()
  }

  const movePrompt = async (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= starterPromptsDraft.length) return

    const copy = [...starterPromptsDraft]
    const [item] = copy.splice(index, 1)
    copy.splice(nextIndex, 0, item)
    setStarterPromptsDraft(copy)

    try {
      await persistContentDraft({ starterPrompts: copy }, 'prompts')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reorder prompts')
    }
  }

  const addStarterPrompt = () => {
    if (starterPromptsDraft.some((prompt) => !isPromptComplete(prompt))) {
      toast.error('Label and context are required for each starter prompt.')
      return
    }
    setStarterPromptsDraft((prev) => [...prev, createPromptDraft()])
  }

  const removeStarterPrompt = async (index: number) => {
    const previousPrompts = starterPromptsDraft
    const nextPrompts = previousPrompts.filter((_, itemIndex) => itemIndex !== index)
    setStarterPromptsDraft(nextPrompts)
    try {
      await persistContentDraft({ starterPrompts: nextPrompts }, 'prompts')
    } catch (err) {
      setStarterPromptsDraft(previousPrompts)
      toast.error(err instanceof Error ? err.message : 'Failed to remove prompt')
    }
  }

  const saveStarterPromptsIfChanged = async (
    nextPrompts = starterPromptsDraft,
    index?: number,
  ) => {
    if (!classroom) return
    if (typeof index === 'number') {
      const targetPrompt = nextPrompts[index]
      if (!isPromptComplete(targetPrompt)) {
        return
      }
    } else if (nextPrompts.some((prompt) => !isPromptComplete(prompt))) {
      toast.error('Label and context are required for each starter prompt.')
      return
    }
    const currentPrompts = nextPrompts.map(({ id, ...prompt }) => prompt)
    if (JSON.stringify(classroom.starterPrompts ?? []) === JSON.stringify(currentPrompts)) return

    try {
      await persistContentDraft({ starterPrompts: nextPrompts }, 'prompts')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update prompts')
    }
  }

  const removeTopic = async (topic: string) => {
    const nextTopics = topicsDraft.filter((item) => item !== topic)
    setTopicsDraft(nextTopics)
    try {
      await persistContentDraft({ topics: nextTopics }, 'topics')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update topics')
    }
  }

  const handleCopyClassCode = async () => {
    if (!classroom?.classCode) return
    await navigator.clipboard.writeText(classroom.classCode)
    toast.success('Class code copied')
  }

  const handleRenameClass = async () => {
    const token = getAccessToken()
    if (!token || !classroom || !renameDraft.trim() || renameDraft.trim() === classroom.title) return

    setIsSavingRename(true)
    try {
      const updated = await updateClassroom(token, classroom.id, { title: renameDraft.trim() })
      setClassroom(updated)
      setRenameDraft(updated.title)
      setRenameDialogOpen(false)
      toast.success('Class renamed')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rename class')
    } finally {
      setIsSavingRename(false)
    }
  }

  const handleChangeTeacher = async (nextValue: string) => {
    const token = getAccessToken()
    if (!token || !classroom) return

    const nextTeacherId = nextValue === 'unassigned' ? null : nextValue
    if (nextTeacherId === (classroom.primaryTeacherId ?? null)) return

    setIsSavingTeacher(true)
    try {
      const updated = await updateClassroom(token, classroom.id, {
        primaryTeacherId: nextTeacherId,
      })
      setClassroom(updated)
      setTeacherDraftId(updated.primaryTeacherId ?? 'unassigned')
      toast.success(nextTeacherId ? 'Teacher updated' : 'Teacher cleared')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update teacher')
    } finally {
      setIsSavingTeacher(false)
    }
  }

  const resetCreateTeacherForm = () => {
    setTeacherFirstName('')
    setTeacherLastName('')
    setTeacherUsername('')
    setTeacherPassword('')
  }

  const handleTeacherSelect = async (value: string) => {
    if (value === CREATE_TEACHER_VALUE) {
      setCreateTeacherDrawerOpen(true)
      return
    }
    setTeacherDraftId(value)
    await handleChangeTeacher(value)
  }

  const handleCreateTeacher = async () => {
    const token = getAccessToken()
    if (!token) return
    if (!teacherFirstName.trim() || !teacherLastName.trim() || !teacherUsername.trim() || !teacherPassword.trim()) {
      toast.error('First Name, Last Name, Username and Password are required.')
      return
    }

    setIsCreatingTeacher(true)
    try {
      const payload: CreateMemberPayload = {
        givenName: teacherFirstName.trim(),
        surName: teacherLastName.trim(),
        username: teacherUsername.trim(),
        initialPassword: teacherPassword.trim(),
        role: 'TEACHER',
      }
      const created = await createTeacher(token, payload)
      setTeachers((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
      setCreateTeacherDrawerOpen(false)
      resetCreateTeacherForm()
      toast.success('Teacher created')
      setTeacherDraftId(created.userId)
      await handleChangeTeacher(created.userId)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create teacher')
    } finally {
      setIsCreatingTeacher(false)
    }
  }

  if (isLoading) {
    return <ClassDetailSkeleton />
  }

  if (error || !classroom) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error ?? 'Class not found.'}</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center gap-5">
        <Button
          variant="neutral"
          className="h-10 shrink-0 px-3 rounded-[0.9rem]"
          onClick={() => navigate('/admin/my-classes')}
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div>
          <p className="text-[11px] font-heading uppercase tracking-[0.22em] text-foreground/42">
            Classroom
          </p>
          <p className="mt-2 text-[0.95rem] text-foreground/62">
            Share the class code, assign the teacher, and manage classwork.
          </p>
        </div>
      </div>

      <div
        className="mb-6 rounded-[1.4rem] border-[3px] border-nb-border p-5 nb-shadow"
        style={{ backgroundColor: heroBackground ?? 'var(--secondary-background)' }}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className={`text-[11px] font-heading uppercase tracking-[0.16em] ${heroEyebrowClass}`}>
              Classroom
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <h2 className={`font-heading text-[2.125rem] font-bold leading-none ${heroTitleClass}`}>{classroom.title}</h2>
              <Button variant="neutral" size="sm" onClick={() => setRenameDialogOpen(true)}>
                <Pencil className="size-4" />
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Badge variant="neutral" className={heroBadgeClass}>
                <statusMeta.Icon className="size-3.5" />
                {statusMeta.label}
              </Badge>
              <Badge variant="neutral" className={heroBadgeClass}>{materialsDraft.length} files</Badge>
              {classroom.teacherName ? (
                <Badge variant="neutral" className={heroBadgeClass}>
                  <UserRound className="size-3.5" />
                  {classroom.teacherName}
                </Badge>
              ) : (
                <Badge variant="neutral" className={heroBadgeClass}>
                  Need teacher
                </Badge>
              )}
            </div>
          </div>
          <div className="min-w-[220px] rounded-[1.1rem] border-2 border-nb-border bg-background p-4">
            <p className="text-[11px] font-heading uppercase tracking-[0.16em] text-foreground/45">Class Code</p>
            <p
              className="mt-2 text-[2rem] font-bold tracking-[0.18em] text-foreground"
              style={{ fontFamily: '"DM Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
            >
              {classroom.classCode}
            </p>
            <p className="mt-2 text-sm text-foreground/55">Students need this after they sign in.</p>
            <Button variant="neutral" size="sm" className="mt-4" onClick={() => void handleCopyClassCode()}>
              <Copy className="size-4" />
              Copy code
            </Button>
          </div>
        </div>
      </div>

      <NavigationMenu viewport={false} className="mb-6 w-full max-w-fit bg-secondary-bg">
        <NavigationMenuList className="gap-1">
          {([
            { key: 'stream', label: 'Stream', icon: ShieldAlert },
            { key: 'classwork', label: 'Classwork', icon: BookOpenText },
            { key: 'people', label: 'People', icon: Users },
          ] as const).map((tab) => (
            <NavigationMenuItem key={tab.key}>
              <button
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'inline-flex h-10 items-center justify-center gap-2 rounded-base border-2 px-4 py-2 text-sm font-heading transition-colors',
                  activeTab === tab.key
                    ? 'border-border bg-main text-main-foreground'
                    : 'border-transparent bg-secondary-bg text-foreground hover:bg-main hover:text-main-foreground',
                )}
              >
                <tab.icon className="size-4" />
                {tab.label}
              </button>
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
      </NavigationMenu>

      {activeTab === 'people' ? (
        <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <ContentCard title="Teacher">
            <div className="rounded-[1.1rem] border-2 border-nb-border bg-secondary-bg p-4">
              <div className="text-[11px] font-heading uppercase tracking-[0.16em] text-foreground/45">
                Current Teacher
              </div>
              <div className="mt-3 font-heading text-xl text-foreground">
                {classroom.teacherName ?? 'Unassigned'}
              </div>
                <p className="mt-3 text-sm text-foreground/55">
                  {classroom.teacherName
                    ? 'Use the picker below to switch the teacher.'
                    : 'No teacher assigned yet. This class still needs a teacher.'}
                </p>
                <div className="mt-4 flex flex-col gap-3">
                <Select value={teacherDraftId} onValueChange={(value) => void handleTeacherSelect(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teachers.map((teacher) => (
                      <SelectItem key={teacher.userId} value={teacher.userId}>
                        {teacher.name}
                      </SelectItem>
                    ))}
                    {isAdmin ? (
                      <SelectItem value={CREATE_TEACHER_VALUE}>+ Create new teacher</SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>
                {isSavingTeacher ? (
                  <div className="text-xs text-foreground/55">Saving teacher…</div>
                ) : null}
              </div>
            </div>

            <div className="mt-4 grid gap-3">
              <MetricCard label="Students" value={students.length} />
              <MetricCard label="Visible" value={visibleRatio} />
              <MetricCard label="Usage" value={`${classUsageCredits} credits`} />
            </div>
          </ContentCard>

          <ContentCard title="Students">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-foreground/60">
                  Roster is read-only here. Student account actions stay in the Students page.
                </p>
              </div>
              <div className="flex w-full max-w-sm items-start justify-end gap-3">
                <div className="relative min-w-0 flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/35" />
                  <Input
                    value={studentQuery}
                    onChange={(event) => {
                      setStudentQuery(event.target.value)
                      setStudentPage(0)
                    }}
                    placeholder="Search student by name or username..."
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="bg-main text-main-foreground hover:bg-main">
                  <TableHead>Student</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Account Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="py-16 text-center text-sm text-foreground/40">
                      No students found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedStudents.map((student) => (
                    <TableRow key={student.userId}>
                      <TableCell>
                        {(() => {
                          const mascot = resolveStudentMascotDisplay(student)
                          const nameDisplay = resolveStudentNameDisplay(student)
                          const usernameDisplay = resolveStudentUsernameDisplay(student)

                          return (
                            <div className="flex items-center gap-3">
                              {mascot.emoji ? (
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-nb-border bg-secondary-bg text-xl">
                                  {mascot.emoji}
                                </div>
                              ) : null}
                              <div className="min-w-0">
                                <div className="truncate font-base text-sm text-foreground">{nameDisplay}</div>
                                <div className="truncate text-xs text-foreground/45">@{usernameDisplay}</div>
                              </div>
                            </div>
                          )
                        })()}
                      </TableCell>
                      <TableCell>
                        {student.credits ?? 0}
                      </TableCell>
                      <TableCell>
                        <Badge variant={student.isActive ? 'success' : 'muted'} className="lowercase">
                          {student.isActive ? 'active' : 'disabled'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {shouldShowClassStudentPagination(visibleStudents.length) ? (
              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs text-foreground/45">
                  Showing {studentPageStart}-{studentPageEnd} of {visibleStudents.length} students
                </span>
                <Pagination className="w-auto mx-0">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(event) => {
                          event.preventDefault()
                          if (safeStudentPage > 0) setStudentPage((prev) => prev - 1)
                        }}
                        aria-disabled={safeStudentPage === 0}
                        className={safeStudentPage === 0 ? 'pointer-events-none opacity-40' : ''}
                      />
                    </PaginationItem>
                    {studentPageNumbers.map((pageNumber, index) =>
                      pageNumber === 'ellipsis' ? (
                        <PaginationItem key={`student-ellipsis-${index}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            href="#"
                            isActive={pageNumber === safeStudentPage}
                            onClick={(event) => {
                              event.preventDefault()
                              setStudentPage(pageNumber)
                            }}
                          >
                            {pageNumber + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ),
                    )}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(event) => {
                          event.preventDefault()
                          if (safeStudentPage < studentPageCount - 1) setStudentPage((prev) => prev + 1)
                        }}
                        aria-disabled={safeStudentPage >= studentPageCount - 1}
                        className={safeStudentPage >= studentPageCount - 1 ? 'pointer-events-none opacity-40' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            ) : null}
          </ContentCard>
        </div>
      ) : null}

      {activeTab === 'stream' ? (
        <div className="space-y-4">
          <div className="space-y-4">
            
            <div className="space-y-4">
              <ContentCard
                title={
                  <div>
                    <div className="text-[1.05rem] font-heading text-foreground">How students join</div>
                    <div className="mt-1 font-base text-foreground/55">
                      Students join from My Classes with the class code you share here.
                    </div>
                  </div>
                }
              >
                <div className="grid gap-3 lg:grid-cols-3">
                  <MetricCard label="Step 1" value={<span className="text-base">Student signs in</span>} className="bg-sidebar" />
                  <MetricCard label="Step 2" value={<span className="text-base">Student taps Join New Class</span>} className="bg-sidebar" />
                  <MetricCard label="Step 3" value={<span className="text-base">Student enters the class code</span>} className="bg-sidebar" />
                </div>
              </ContentCard>

              <ContentCard
                title={
                  <div>
                    <div className="text-[1.05rem] font-heading text-foreground">At a glance</div>
                    <div className="mt-1 font-base text-foreground/55">
                      The front page stays focused on identity and access.
                    </div>
                  </div>
                }
              >
                <div className="grid gap-3 md:grid-cols-3">
                  <MetricCard label="Materials Published" value={materialsDraft.length} className="bg-sidebar" />
                  <MetricCard
                    label="Teacher Assigned"
                    value={
                      classroom.teacherName ? (
                        <span className="inline-flex items-center gap-2">
                          <UserRound className="size-5 shrink-0" />
                          <span className="truncate">{classroom.teacherName}</span>
                        </span>
                      ) : (
                        'No'
                      )
                    }
                    className="bg-sidebar"
                  />
                  <div />
                </div>
              </ContentCard>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === 'classwork' ? (
        <div className="space-y-4">
          <ContentCard title={renderSavingTitle('Topics', isSavingTopics)}>
            <div className="mb-3 flex flex-wrap gap-2">
              {topicsDraft.map((topic) => (
                <Badge
                  key={topic}
                  className="gap-2 border-black px-3 py-1 text-sm"
                  style={{
                    backgroundColor: getClassroomPalette({ title: topic }).light,
                    color: '#111111',
                  }}
                >
                  {topic}
                  <button type="button" onClick={() => void removeTopic(topic)}>
                    <X className="size-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex max-w-md gap-2">
              <Input
                value={topicInput}
                onChange={(event) => setTopicInput(event.target.value.slice(0, TOPIC_CHAR_LIMIT))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    void addTopic()
                  }
                }}
                placeholder="Add a topic..."
                maxLength={TOPIC_CHAR_LIMIT}
              />
              <Button variant="neutral" onClick={addTopic} disabled={!topicInput.trim()}>
                <Plus className="size-4" />
                Add
              </Button>
            </div>
            <div className="mt-2 text-xs text-foreground/45">
              Up to {TOPIC_LIMIT} topics, {topicInput.length}/{TOPIC_CHAR_LIMIT} characters.
            </div>
          </ContentCard>

          <ContentCard title={renderSavingTitle('Student Starter Prompts', isSavingPrompts)}>
            <div className="mb-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 text-sm text-foreground/60">
                <span>Shown to students</span>
                <Badge variant="success">
                  Active
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral"
                  onClick={addStarterPrompt}
                >
                  <Plus className="size-4" />
                  Add Prompt
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {starterPromptsDraft.map((prompt, index) => (
                <div key={prompt.id} className="rounded-base border-2 border-nb-border bg-secondary-bg p-3">
                  <div className="grid gap-2 md:grid-cols-[auto_minmax(0,1fr)_auto]">
                    {starterPromptsDraft.length > 1 ? (
                      <div className="flex items-center gap-2">
                        <Button variant="neutral" size="sm" onClick={() => movePrompt(index, -1)} disabled={index === 0}>
                          <ArrowUp className="size-4" />
                        </Button>
                        <Button variant="neutral" size="sm" onClick={() => movePrompt(index, 1)} disabled={index === starterPromptsDraft.length - 1}>
                          <ArrowDown className="size-4" />
                        </Button>
                      </div>
                    ) : (
                      <div />
                    )}
                    <Input
                      value={prompt.label}
                      onChange={(event) =>
                        setStarterPromptsDraft((prev) =>
                          prev.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, label: event.target.value } : item,
                          ),
                        )}
                      onBlur={(event) => {
                        const nextPrompts = starterPromptsDraft.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, label: event.target.value } : item,
                        )
                        void saveStarterPromptsIfChanged(nextPrompts, index)
                      }}
                      placeholder="Prompt label"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        variant="neutral"
                        onClick={() => void removeStarterPrompt(index)}
                      >
                        <Trash2 className="size-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                  <textarea
                    value={prompt.prompt}
                    onChange={(event) =>
                      setStarterPromptsDraft((prev) =>
                        prev.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, prompt: event.target.value } : item,
                        ),
                      )}
                    onBlur={(event) => {
                      const nextPrompts = starterPromptsDraft.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, prompt: event.target.value } : item,
                      )
                      void saveStarterPromptsIfChanged(nextPrompts, index)
                    }}
                    placeholder="Full prompt question..."
                    className="mt-2 min-h-24 w-full rounded-base border-2 border-nb-border bg-background px-3 py-2 text-sm text-foreground outline-none"
                  />
                  {!isPromptComplete(prompt) ? (
                    <p className="mt-2 text-xs text-destructive/80">
                      Label and context are required.
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </ContentCard>

          <ContentCard title={renderSavingTitle('Materials', isSavingMaterials)}>
            {!showMaterialForm ? (
              <div className="mb-5 flex justify-end">
                <Button
                  variant="neutral"
                  onClick={() => {
                    resetMaterialForm()
                    setShowMaterialForm(true)
                  }}
                >
                  <Plus className="size-4" />
                  Add Material
                </Button>
              </div>
            ) : (
              <div className="mb-5 rounded-base border-2 border-nb-border bg-secondary-bg p-4">
                <div className="grid gap-3">
                  <Input
                    value={materialTitle}
                    onChange={(event) => setMaterialTitle(event.target.value)}
                    placeholder="Title"
                  />
                  <textarea
                    value={materialContent}
                    onChange={(event) => setMaterialContent(event.target.value)}
                    rows={6}
                    className="min-h-28 rounded-base border-2 border-nb-border bg-background px-3 py-2 text-sm outline-none focus:border-main"
                    placeholder="Content — notes, key facts, questions..."
                  />
                  <button
                    type="button"
                    onClick={handleMaterialAttachmentUpload}
                    className="flex items-center gap-2 rounded-base border-2 border-dashed border-nb-border px-3 py-3 text-sm text-foreground/55 transition-colors hover:bg-background"
                  >
                    <Paperclip className="size-4" />
                    {isUploadingMaterialAttachment
                      ? 'Uploading attachment...'
                      : materialAttachment
                        ? materialAttachment.filename
                        : 'Attach a file (TXT, PDF, image — max 1 MB)'}
                  </button>
                  {!materialAttachment ? (
                    <p className="text-xs text-destructive/80">
                      Attachment is required.
                    </p>
                  ) : null}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Select value={materialKind} onValueChange={(value) => setMaterialKind(value as MaterialKind)}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NOTE">Note</SelectItem>
                        <SelectItem value="REFERENCE">Reference</SelectItem>
                        <SelectItem value="WORKSHEET">Worksheet</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <Button variant="neutral" onClick={resetMaterialForm}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveMaterial} disabled={!materialTitle.trim() || !materialAttachment || isUploadingMaterialAttachment}>
                        <Plus className="size-4" />
                        {editingMaterialIndex != null ? 'Save' : 'Add'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {materialsDraft.length === 0 ? (
              <div className="text-sm text-foreground/45">No materials configured.</div>
            ) : (
              <div className="grid gap-3 lg:grid-cols-2">
                {materialsDraft.map((material, index) => (
                  <div key={material.id} className="rounded-base border-2 border-nb-border bg-secondary-bg p-4">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className={cn(
                          'flex size-8 shrink-0 items-center justify-center rounded-base border-2 border-nb-border',
                          materialKindIconContainerClass(material.kind),
                        )}>
                          <div className={materialKindIconClass(material.kind)}>
                            <MaterialTypeIcon type={material.kind} />
                          </div>
                        </div>
                        <div className="min-w-0 pt-0.5">
                          <div className="line-clamp-1 font-heading text-lg leading-tight text-foreground">
                            {material.title}
                          </div>
                          {material.kind ? (
                            <div className="mt-2">
                              <Badge variant="muted" className={cn('normal-case', materialKindBadgeClass(material.kind))}>
                                {materialKindLabel(material.kind)}
                              </Badge>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="neutral" size="sm" onClick={() => handleEditMaterial(material, index)}>
                          <Pencil className="size-4" />
                        </Button>
                        <Button variant="neutral" size="sm" onClick={() => handleDeleteMaterial(index)}>
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    <div className="line-clamp-4 whitespace-pre-wrap text-sm leading-6 text-foreground/75">
                      {material.content ?? material.attachment?.filename ?? 'No content provided.'}
                    </div>
                    {material.attachment ? (
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground/55">
                        <button
                          type="button"
                          className="inline-flex items-center"
                          onClick={() => openMaterialPreview(material.attachment!)}
                          disabled={!material.attachment.mimeType.startsWith('image/') && !isPreviewableFile(material.attachment.mimeType, material.attachment.filename)}
                        >
                          <Badge variant="muted" className="normal-case">
                            {material.attachment.filename}
                          </Badge>
                        </button>
                        {material.attachment.mimeType.startsWith('image/') ? (
                          <ImageIcon className="size-3.5" />
                        ) : (
                          <FileText className="size-3.5" />
                        )}
                        {material.attachment.sizeBytes ? (
                          <span>{Math.ceil(material.attachment.sizeBytes / 1024)} KB</span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </ContentCard>

        </div>
      ) : null}
      {filePreview ? (
        <FilePreview
          src={filePreview.src}
          fileName={filePreview.name}
          mimeType={filePreview.mimeType}
          open={true}
          onClose={() => setFilePreview(null)}
        />
      ) : null}
      {imagePreview ? (
        <ImagePreview
          src={imagePreview}
          open={true}
          onClose={() => setImagePreview(null)}
        />
      ) : null}
      <Drawer direction="right" open={createTeacherDrawerOpen} onOpenChange={setCreateTeacherDrawerOpen}>
        <DrawerContent className="sm:max-w-md">
          <DrawerHeader>
            <DrawerTitle>Create teacher</DrawerTitle>
            <DrawerDescription>
              Add a new teacher account, then assign it to this class.
            </DrawerDescription>
          </DrawerHeader>

          <div className="grid gap-4 px-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                  First Name
                </label>
                <Input value={teacherFirstName} onChange={(e) => setTeacherFirstName(e.target.value)} />
              </div>
              <div className="grid gap-1.5">
                <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                  Last Name
                </label>
                <Input value={teacherLastName} onChange={(e) => setTeacherLastName(e.target.value)} />
              </div>
            </div>

            <div className="grid gap-1.5">
              <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                Username
              </label>
              <Input value={teacherUsername} onChange={(e) => setTeacherUsername(e.target.value)} />
            </div>

            <div className="grid gap-1.5">
              <label className="text-xs font-heading uppercase tracking-wider text-foreground/60">
                Password
              </label>
              <div className="flex gap-2">
                <Input
                  value={teacherPassword}
                  onChange={(e) => setTeacherPassword(e.target.value)}
                  placeholder="••••••••"
                  className="flex-1"
                />
                <Button variant="neutral" size="icon" className="size-9 shrink-0" onClick={() => setTeacherPassword(genTempPassword())}>
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>

          <DrawerFooter>
            <Button variant="neutral" onClick={() => setCreateTeacherDrawerOpen(false)} disabled={isCreatingTeacher}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreateTeacher()} disabled={isCreatingTeacher}>
              {isCreatingTeacher ? 'Creating…' : 'Create teacher'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Rename class</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Input
              value={renameDraft}
              onChange={(event) => setRenameDraft(event.target.value)}
              placeholder="Rename class"
            />
            <p className="text-sm text-foreground/60">
              Update the class title shown to teachers and students.
            </p>
          </div>
          <DialogFooter>
            <Button variant="neutral" size="sm" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => void handleRenameClass()} disabled={isSavingRename || !renameDraft.trim()}>
              {isSavingRename ? 'Saving…' : 'Save name'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
